<script setup>
/**
 * Ticket types — how a workspace models its own process.
 *
 * One screen, two things: the catalogue of types (who sees them, whether they
 * talk to the customer) and the configuration of the type currently open. They
 * are on the same page because they are the same decision — a type without its
 * stages is half an answer.
 *
 * The open type's configuration has two halves, behind one tab bar: the rail
 * says how work moves, the fields say what the work is about. They are tabs
 * rather than two stacked sections because an admin is editing one or the
 * other, never both at once, and the rail is long enough already.
 *
 * The server owns every rule this screen touches. Visibility comes back as a
 * computed verdict, guard rails ("a type needs a way in and a way out", "these
 * tickets have to land somewhere") arrive as refusals, and each of them is shown
 * verbatim rather than re-implemented here. Every write answers with the whole
 * type, rail included, so nothing on screen has to refetch to stay in step.
 *
 * The fallback inbox is account configuration rather than a type's, but it lives
 * here because it is the same decision: a type that keeps the customer posted
 * has to be able to reach them when the conversation cannot take the message.
 * Unset is honest — the engine picks no inbox of its own and records the ticket
 * unreachable instead.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import VisibilityBadge from 'dashboard/components-next/SupportCases/VisibilityBadge.vue';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';

import FieldDialog from './FieldDialog.vue';
import FieldsEditor from './FieldsEditor.vue';
import StageDialog from './StageDialog.vue';
import StagesEditor from './StagesEditor.vue';
import TicketTypeDialog from './TicketTypeDialog.vue';

const { t } = useI18n();
const store = useStore();
const casesStore = useSupportCasesStore();
const { currentAccount, updateAccount } = useAccount();

const teams = useMapGetter('teams/getTeams');
const inboxes = useMapGetter('inboxes/getInboxes');

const typeDialog = ref(null);
const stageDialog = ref(null);
const stageRemovalDialog = ref(null);
const fieldDialog = ref(null);
const showArchiveConfirm = ref(false);
const showFieldRemoveConfirm = ref(false);
const pendingType = ref(null);
const pendingStage = ref(null);
const pendingField = ref(null);
const removalTargetId = ref(null);
const selectedTypeId = ref(null);
const activeConfigTab = ref(0);
const isLoading = ref(true);

const caseTypes = computed(() => casesStore.caseTypes);
const isSaving = computed(
  () => casesStore.uiFlags.isCreatingItem || casesStore.uiFlags.isUpdatingItem
);

// The rail belongs to whichever type is open. Falling back to the first type
// means the editor is never empty on a workspace that has any type at all.
const selectedType = computed(
  () =>
    caseTypes.value.find(type => type.id === selectedTypeId.value) ||
    caseTypes.value[0] ||
    null
);

const stages = computed(() => selectedType.value?.workflowStages || []);

const fieldDefinitions = computed(
  () => selectedType.value?.fieldDefinitions || []
);

// Counts on the tabs, because "how many stages does this type have" and "does
// it ask for anything" are the two questions the tab bar can answer for free.
const configTabs = computed(() => [
  {
    label: t('SUPPORT_CASES.SETTINGS.STAGES.TITLE'),
    count: stages.value.length,
  },
  {
    label: t('SUPPORT_CASES.SETTINGS.FIELDS.TITLE'),
    count: fieldDefinitions.value.length,
  },
]);

// A stage cannot be moved onto itself, so the target list is the rest of the rail.
const removalTargets = computed(() =>
  stages.value
    .filter(stage => stage.id !== pendingStage.value?.id)
    .map(stage => ({ value: stage.id, label: stage.name }))
);

const pendingOpenCount = computed(() => pendingStage.value?.openCaseCount || 0);

// Email only: the fallback publishes by opening an email conversation with the
// requester, and every other channel closes its reply window on the customer's
// own clock, which is the situation the fallback exists to answer.
const fallbackInboxOptions = computed(() => [
  { value: '', label: t('SUPPORT_CASES.SETTINGS.FALLBACK.NONE') },
  ...inboxes.value
    .filter(inbox => inbox.channel_type === INBOX_TYPES.EMAIL)
    .map(inbox => ({ value: inbox.id, label: inbox.name })),
]);

const fallbackInboxId = ref('');

watch(
  currentAccount,
  account => {
    fallbackInboxId.value =
      account?.settings?.support_case_fallback_inbox_id || '';
  },
  { immediate: true }
);

const onSelectFallbackInbox = async value => {
  const previous = fallbackInboxId.value;
  fallbackInboxId.value = value;
  try {
    await updateAccount(
      { support_case_fallback_inbox_id: value || null },
      { silent: true }
    );
    useAlert(t('SUPPORT_CASES.SETTINGS.FALLBACK.SAVED'));
  } catch (error) {
    fallbackInboxId.value = previous;
    useAlert(t('SUPPORT_CASES.SETTINGS.FALLBACK.ERROR'));
  }
};

// A stage with nothing on it needs no target; one with open tickets does, and the
// server refuses without it. The dialog asks for the target only when there is
// somewhere to move to, and states the count so the choice is made in view of it.
const canConfirmRemoval = computed(
  () =>
    !pendingOpenCount.value ||
    (Boolean(removalTargetId.value) && removalTargets.value.length > 0)
);

onMounted(async () => {
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
  await casesStore.fetchCaseTypes({ force: true });
  selectedTypeId.value = caseTypes.value[0]?.id || null;
  isLoading.value = false;
});

// Guard-rail refusals are the point of this screen, so they are shown as the
// server phrased them - it knows which rule was broken and this file does not.
const run = async (work, successMessage) => {
  try {
    await work();
    useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(error?.message || '');
    return false;
  }
};

const onSubmitType = async ({ id, payload }) => {
  const saved = await run(
    () =>
      id
        ? casesStore.updateCaseType(id, payload)
        : casesStore.createCaseType(payload),
    t('SUPPORT_CASES.SETTINGS.FORM.SAVED')
  );
  if (!saved) return;

  if (!id) selectedTypeId.value = caseTypes.value.at(-1)?.id || null;
  typeDialog.value?.close();
};

const onArchiveType = async () => {
  const archived = await run(
    () => casesStore.archiveCaseType(pendingType.value.id),
    t('SUPPORT_CASES.SETTINGS.LIST.ARCHIVED')
  );
  showArchiveConfirm.value = false;
  if (archived && selectedTypeId.value === pendingType.value?.id) {
    selectedTypeId.value = caseTypes.value[0]?.id || null;
  }
  pendingType.value = null;
};

const onSubmitStage = async ({ id, payload }) => {
  const saved = await run(
    () =>
      id
        ? casesStore.updateStage(selectedType.value.id, id, payload)
        : casesStore.addStage(selectedType.value.id, payload),
    t('SUPPORT_CASES.SETTINGS.STAGES.SAVED')
  );
  if (saved) stageDialog.value?.close();
};

const onRemoveStage = async () => {
  const removed = await run(
    () =>
      casesStore.archiveStage(selectedType.value.id, pendingStage.value.id, {
        targetCaseStageId: removalTargetId.value,
      }),
    t('SUPPORT_CASES.SETTINGS.STAGES.REMOVED')
  );
  if (!removed) return;

  stageRemovalDialog.value?.close();
  pendingStage.value = null;
};

const onReorderStages = stageIds =>
  run(
    () => casesStore.reorderStages(selectedType.value.id, stageIds),
    t('SUPPORT_CASES.SETTINGS.STAGES.REORDERED')
  );

const onSubmitField = async ({ id, payload }) => {
  const saved = await run(
    () =>
      id
        ? casesStore.updateFieldDefinition(selectedType.value.id, id, payload)
        : casesStore.addFieldDefinition(selectedType.value.id, payload),
    t('SUPPORT_CASES.SETTINGS.FIELDS.SAVED')
  );
  if (saved) fieldDialog.value?.close();
};

// Archival: the values already collected under this key stay on their tickets,
// which is what the confirmation says rather than implying a delete.
const onRemoveField = async () => {
  await run(
    () =>
      casesStore.archiveFieldDefinition(
        selectedType.value.id,
        pendingField.value.id
      ),
    t('SUPPORT_CASES.SETTINGS.FIELDS.REMOVED')
  );
  showFieldRemoveConfirm.value = false;
  pendingField.value = null;
};

const onReorderFields = definitionIds =>
  run(
    () =>
      casesStore.reorderFieldDefinitions(selectedType.value.id, definitionIds),
    t('SUPPORT_CASES.SETTINGS.FIELDS.REORDERED')
  );

const openTypeDialog = (caseType = null) => typeDialog.value?.open(caseType);

const openStageDialog = (stage = null) => stageDialog.value?.open(stage);

const confirmArchiveType = caseType => {
  pendingType.value = caseType;
  showArchiveConfirm.value = true;
};

const confirmRemoveStage = stage => {
  pendingStage.value = stage;
  removalTargetId.value = null;
  stageRemovalDialog.value?.open();
};

// The tab bar hands back the tab it switched to; the editors below key off its
// position, so the label is what resolves it.
const onConfigTabChanged = tab => {
  const index = configTabs.value.findIndex(entry => entry.label === tab?.label);
  activeConfigTab.value = index === -1 ? 0 : index;
};

const openFieldDialog = (definition = null) =>
  fieldDialog.value?.open(definition);

const confirmRemoveField = definition => {
  pendingField.value = definition;
  showFieldRemoveConfirm.value = true;
};
</script>

<template>
  <SettingsPageShell
    wide
    :title="t('SUPPORT_CASES.SETTINGS.HEADER.TITLE')"
    :description="t('SUPPORT_CASES.SETTINGS.HEADER.DESCRIPTION')"
  >
    <template #actions>
      <Button
        size="sm"
        icon="i-lucide-plus"
        :label="t('SUPPORT_CASES.SETTINGS.LIST.NEW')"
        data-testid="ticket-type-new"
        @click="openTypeDialog()"
      />
    </template>

    <SettingsSection
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('SKELETON.LOADING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <p
      v-else-if="!caseTypes.length"
      class="py-20 text-base text-center text-n-slate-11"
    >
      {{ t('SUPPORT_CASES.SETTINGS.LIST.EMPTY_DESCRIPTION') }}
    </p>

    <div v-else class="flex flex-col gap-8">
      <ul class="flex flex-col gap-2" data-testid="ticket-type-list">
        <li
          v-for="caseType in caseTypes"
          :key="caseType.id"
          class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer border-n-weak"
          :class="
            selectedType && selectedType.id === caseType.id
              ? 'bg-n-alpha-1'
              : ''
          "
          :data-testid="`ticket-type-row-${caseType.id}`"
          @click="selectedTypeId = caseType.id"
        >
          <div class="flex flex-col min-w-0 gap-1">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ caseType.name }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{ caseType.description }}
            </span>
          </div>

          <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
            <VisibilityBadge
              :mode="caseType.visibility && caseType.visibility.mode"
              :team-names="
                (caseType.visibility && caseType.visibility.teamNames) || []
              "
            />
            <span
              class="text-xs tabular-nums text-n-slate-11"
              :data-testid="`ticket-type-usage-${caseType.id}`"
            >
              {{
                t('SUPPORT_CASES.SETTINGS.LIST.OPEN_TICKETS', {
                  count: caseType.openCaseCount || 0,
                })
              }}
            </span>
            <span
              v-if="caseType.shareProgressWithCustomer"
              class="text-n-teal-10 i-lucide-megaphone size-4"
              :title="t('SUPPORT_CASES.SETTINGS.FORM.PROGRESS_TITLE')"
              :data-testid="`ticket-type-progress-${caseType.id}`"
            />
            <Button
              variant="ghost"
              color="slate"
              size="xs"
              icon="i-lucide-pencil"
              :aria-label="t('SUPPORT_CASES.SETTINGS.LIST.EDIT')"
              :data-testid="`ticket-type-edit-${caseType.id}`"
              @click.stop="openTypeDialog(caseType)"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-archive"
              :aria-label="t('SUPPORT_CASES.SETTINGS.LIST.ARCHIVE')"
              :data-testid="`ticket-type-archive-${caseType.id}`"
              @click.stop="confirmArchiveType(caseType)"
            />
          </div>
        </li>
      </ul>

      <div
        v-if="selectedType"
        class="flex flex-col gap-4"
        data-testid="ticket-type-config"
      >
        <TabBar
          :tabs="configTabs"
          :initial-active-tab="activeConfigTab"
          data-testid="ticket-type-tabs"
          @tab-changed="onConfigTabChanged"
        />

        <StagesEditor
          v-if="activeConfigTab === 0"
          :stages="stages"
          :share-progress-with-customer="
            selectedType.shareProgressWithCustomer || false
          "
          :disabled="isSaving"
          @add="openStageDialog()"
          @edit="openStageDialog($event)"
          @remove="confirmRemoveStage($event)"
          @reorder="onReorderStages($event)"
        />

        <FieldsEditor
          v-else
          :field-definitions="fieldDefinitions"
          :disabled="isSaving"
          @add="openFieldDialog()"
          @edit="openFieldDialog($event)"
          @remove="confirmRemoveField($event)"
          @reorder="onReorderFields($event)"
        />
      </div>
    </div>

    <SettingsSection
      v-if="!isLoading"
      :title="t('SUPPORT_CASES.SETTINGS.FALLBACK.TITLE')"
      :description="t('SUPPORT_CASES.SETTINGS.FALLBACK.DESCRIPTION')"
    >
      <div
        class="flex items-center justify-between gap-4 p-4"
        data-testid="ticket-fallback-inbox"
      >
        <div class="flex flex-col min-w-0 gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('SUPPORT_CASES.SETTINGS.FALLBACK.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('SUPPORT_CASES.SETTINGS.FALLBACK.HINT') }}
          </span>
        </div>
        <Select
          :model-value="fallbackInboxId"
          :options="fallbackInboxOptions"
          class="flex-shrink-0"
          @update:model-value="onSelectFallbackInbox"
        />
      </div>
    </SettingsSection>
  </SettingsPageShell>

  <TicketTypeDialog
    ref="typeDialog"
    :teams="teams"
    :is-submitting="isSaving"
    @submit="onSubmitType"
  />

  <StageDialog
    ref="stageDialog"
    :share-progress-with-customer="
      (selectedType && selectedType.shareProgressWithCustomer) || false
    "
    :is-submitting="isSaving"
    @submit="onSubmitStage"
  />

  <FieldDialog
    ref="fieldDialog"
    :is-submitting="isSaving"
    @submit="onSubmitField"
  />

  <ConfirmDialog
    v-model:show="showFieldRemoveConfirm"
    :title="t('SUPPORT_CASES.SETTINGS.FIELDS.REMOVE_TITLE')"
    :description="t('SUPPORT_CASES.SETTINGS.FIELDS.REMOVE_DESCRIPTION')"
    :confirm-button-label="t('SUPPORT_CASES.SETTINGS.FIELDS.REMOVE_CONFIRM')"
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    @confirm="onRemoveField"
  />

  <ConfirmDialog
    v-model:show="showArchiveConfirm"
    :title="t('SUPPORT_CASES.SETTINGS.LIST.ARCHIVE_CONFIRM_TITLE')"
    :description="t('SUPPORT_CASES.SETTINGS.LIST.ARCHIVE_CONFIRM_DESCRIPTION')"
    :confirm-button-label="t('SUPPORT_CASES.SETTINGS.LIST.ARCHIVE_CONFIRM')"
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    @confirm="onArchiveType"
  />

  <Dialog
    ref="stageRemovalDialog"
    :title="t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_TITLE')"
    :description="t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_DESCRIPTION')"
    :confirm-button-label="t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_CONFIRM')"
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    :disable-confirm-button="!canConfirmRemoval"
    :is-loading="isSaving"
    @confirm="onRemoveStage"
  >
    <div class="flex flex-col gap-3" data-testid="stage-removal">
      <p
        v-if="pendingOpenCount"
        class="mb-0 text-sm text-n-slate-11"
        data-testid="stage-removal-usage"
      >
        {{
          t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_USAGE', {
            count: pendingOpenCount,
          })
        }}
      </p>

      <div
        v-if="removalTargets.length"
        class="flex flex-col gap-1"
        data-testid="stage-removal-target"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_TARGET_LABEL') }}
        </label>
        <Select v-model="removalTargetId" :options="removalTargets" />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.REMOVE_TARGET_HINT') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
